import { GameScene, InputState, GameObject, GameConfig } from '../types/game.types';
import { RenderManager } from '../core/RenderManager';
import { AudioManager } from '../core/AudioManager';
import { LeaderboardManager } from '../core/LeaderboardManager';
import { InitialEntry } from '../ui/InitialEntry';

export abstract class BaseGame implements GameScene {
  protected renderManager: RenderManager;
  protected audioManager: AudioManager;
  protected config: GameConfig;
  protected gameObjects: GameObject[] = [];
  protected nextObjectId: number = 1;
  protected leaderboardManager: LeaderboardManager;
  protected initialEntry: InitialEntry | null = null;
  
  // Game state
  protected score: number = 0;
  protected lives: number = 3;
  protected level: number = 1;
  protected isGameOver: boolean = false;
  protected isPaused: boolean = false;
  protected isHighScore: boolean = false;
  protected showingInitialEntry: boolean = false;
  protected gameId: string = '';

  constructor(renderManager: RenderManager, audioManager: AudioManager, config: GameConfig, gameId: string) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.config = config;
    this.gameId = gameId;
    this.leaderboardManager = LeaderboardManager.getInstance();
  }

  public abstract init(): void;
  public abstract update(deltaTime: number): void;
  public abstract handleInput(input: InputState): void;

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Render all game objects
    this.gameObjects.forEach(obj => {
      if (obj.active) {
        obj.render(ctx);
      }
    });
    
    // Render HUD
    this.renderHUD();
    
    // Render game over screen if needed
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      console.log('Rendering initial entry component');
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    } else if (this.showingInitialEntry) {
      console.log('showingInitialEntry is true but initialEntry is null');
    }
  }

  public updateBase(deltaTime: number, input: InputState): void {
    if (this.showingInitialEntry && this.initialEntry) {
      console.log('updateBase: calling initialEntry.update');
      this.initialEntry.update(deltaTime, input);
      return;
    }
    
    if (!this.isGameOver && !this.isPaused) {
      this.update(deltaTime);
    }
    
    // Only handle input if we're not showing the initial entry
    if (!this.showingInitialEntry) {
      this.handleInput(input);
    }
  }

  protected renderHUD(): void {
    const hudY = 2;
    const color = '#FFFFFF';
    const font = '12px "Courier New", monospace';

    // Score
    this.renderManager.drawText(`SCORE: ${this.score.toString().padStart(6, '0')}`, 5, hudY, color, font);

    // Lives
    const livesX = this.config.width - 120;
    this.renderManager.drawText(`LIVES: `, livesX, hudY, color, font);

    // Draw ship icons for remaining lives
    for (let i = 0; i < this.lives; i++) {
      const shipX = livesX + 50 + (i * 15);
      this.renderManager.drawC64Char('▲', shipX, hudY, '#62D0FE', 0.8);
    }

    // Level
    const centerX = this.config.width / 2;
    this.renderManager.drawTextCentered(`LEVEL ${this.level}`, centerX, hudY, color, font);
  }

  protected renderGameOver(): void {
    if (this.showingInitialEntry) {
      console.log('renderGameOver: skipping because showing initial entry');
      return;
    }
    console.log('renderGameOver: rendering normal game over screen');
    
    // Semi-transparent overlay
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.8)');
    
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    // Game Over text
    this.renderManager.drawTextCentered('GAME OVER', centerX, centerY - 60, '#883932', '32px "Courier New", monospace');
    
    // Final score
    this.renderManager.drawTextCentered(`FINAL SCORE: ${this.score}`, centerX, centerY - 20, '#BFCE72', '18px "Courier New", monospace');
    
    // High score message if applicable
    if (this.isHighScore) {
      this.renderManager.drawTextCentered('NEW HIGH SCORE!', centerX, centerY + 10, '#FFFF00', '20px "Courier New", monospace');
      
      const rank = this.leaderboardManager.getRank(this.gameId, this.score);
      this.renderManager.drawTextCentered(`RANK #${rank}`, centerX, centerY + 35, '#62D0FE', '16px "Courier New", monospace');
    }
    
    // Instructions
    this.renderManager.drawTextCentered('PRESS SPACE TO RESTART', centerX, centerY + 60, '#FFFFFF', '14px "Courier New", monospace');
    this.renderManager.drawTextCentered('PRESS ESC FOR MAIN MENU', centerX, centerY + 80, '#787878', '14px "Courier New", monospace');
  }

  // Game object management
  protected addGameObject(obj: GameObject): void {
    this.gameObjects.push(obj);
  }

  protected removeGameObject(id: string): void {
    const index = this.gameObjects.findIndex(obj => obj.id === id);
    if (index !== -1) {
      this.gameObjects.splice(index, 1);
    }
  }

  protected getGameObjects(): GameObject[] {
    return this.gameObjects.filter(obj => obj.active);
  }

  protected getGameObjectsByType<T extends GameObject>(type: new (...args: any[]) => T): T[] {
    return this.gameObjects.filter((obj): obj is T => obj instanceof type && obj.active);
  }

  protected generateObjectId(): string {
    return `obj_${this.nextObjectId++}`;
  }

  // Utility methods for collision detection
  protected checkCollision(obj1: GameObject, obj2: GameObject): boolean {
    const bounds1 = obj1.getBounds();
    const bounds2 = obj2.getBounds();
    
    return bounds1.x < bounds2.x + bounds2.width &&
           bounds1.x + bounds1.width > bounds2.x &&
           bounds1.y < bounds2.y + bounds2.height &&
           bounds1.y + bounds1.height > bounds2.y;
  }

  protected isOffScreen(obj: GameObject): boolean {
    const bounds = obj.getBounds();
    return bounds.x + bounds.width < 0 || 
           bounds.x > this.config.width || 
           bounds.y + bounds.height < 0 || 
           bounds.y > this.config.height;
  }

  // Game state methods
  public getScore(): number {
    return this.score;
  }

  public addScore(points: number): void {
    this.score += points;
  }

  public getLives(): number {
    return this.lives;
  }

  public loseLife(): void {
    this.lives--;
    if (this.lives <= 0) {
      this.gameOver();
    }
  }

  public addLife(): void {
    this.lives++;
  }

  public getLevel(): number {
    return this.level;
  }

  public nextLevel(): void {
    this.level++;
  }

  protected gameOver(): void {
    console.log('BaseGame.gameOver() called');
    this.isGameOver = true;
    this.audioManager.playGameOver();
    this.checkHighScore();
  }

  private checkHighScore(): void {
    console.log(`Checking high score: gameId=${this.gameId}, score=${this.score}`);
    this.isHighScore = this.leaderboardManager.isHighScore(this.gameId, this.score);
    console.log(`Is high score: ${this.isHighScore}`);
    
    if (this.isHighScore) {
      console.log('Showing initial entry');
      this.showInitialEntry();
    } else {
      console.log('Not a high score, showing normal game over');
    }
  }

  private showInitialEntry(): void {
    console.log('BaseGame.showInitialEntry() called');
    this.showingInitialEntry = true;
    console.log('showingInitialEntry set to true');
    this.initialEntry = new InitialEntry(
      this.renderManager,
      this.audioManager,
      (initials: string) => {
        console.log(`Initial entry completed with initials: ${initials}`);
        this.saveHighScore(initials);
        this.hideInitialEntry();
      }
    );
    console.log('InitialEntry component created');
  }

  private saveHighScore(initials: string): void {
    this.leaderboardManager.addScore(this.gameId, initials, this.score, this.level);
  }

  private hideInitialEntry(): void {
    this.showingInitialEntry = false;
    this.initialEntry = null;
  }

  public restart(): void {
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.isPaused = false;
    this.isHighScore = false;
    this.showingInitialEntry = false;
    this.initialEntry = null;
    this.gameObjects = [];
    this.nextObjectId = 1;
    this.init();
  }

  public cleanup(): void {
    this.gameObjects = [];
  }
}